import { isEmpty } from "lodash";
import { ClaimInfo } from "src/components/shared/card/types";
import { ClaimDataObj, ClaimData } from "src/components/shared/claimTab/types";
import { QueueQueryRecord } from "src/containers/queue/types";
import { GenericObject } from "src/defs";
import {
  AnalyticsCard,
  ClientFilterConfig,
  QueueConfig,
} from "src/services/types";
import { clearTypeNameKey } from "src/utils/transformers";
import {
  pickDynamicFilterPatch,
  pickLegacyQueueFilterValues,
  normalizeProcedureType,
  RESERVED_QUEUE_SCALAR_FIELDS,
} from "src/components/shared/dynamicFilters/helpers";
import { getTabFilterKey, getTabs } from "src/shared/home-helper";

/** Queue filterKey / GraphQL needsHumanRsReview for escalated RS claims. */
export const QUEUE_TAB_NEEDS_HUMAN_RS_REVIEW = "needs_human_rs_review";

/** Matches backend commonconsts.IsRecordSpecialist (role suffix). */
const RECORD_SPECIALIST_ROLE_SUFFIX = "_RECORD_SPECIALIST";

export function isRecordSpecialistRole(roles?: string[] | null): boolean {
  return (roles || []).some(
    (role) =>
      typeof role === "string" && role.endsWith(RECORD_SPECIALIST_ROLE_SUFFIX)
  );
}

/** True when claimLevelQueueConfig.tableTabs has any tabs. */
export function hasQueueTableTabs(queueConfig?: QueueConfig | null): boolean {
  return !!(queueConfig?.tableTabs && queueConfig.tableTabs.length > 0);
}

/**
 * True when Queue tabs include the Escalated (`needs_human_rs_review`) filter.
 * Other future tabs can exist without enabling this GraphQL filter.
 */
export function isHumanRsReviewFilterEnabled(
  queueConfig?: QueueConfig | null
): boolean {
  return (queueConfig?.tableTabs || []).some(
    (tab) => getTabFilterKey(tab) === QUEUE_TAB_NEEDS_HUMAN_RS_REVIEW
  );
}

/**
 * Escalated UI + query filter: config has Escalated tab and user is Record Specialist.
 */
export function isHumanRsReviewUiEnabled(
  queueConfig?: QueueConfig | null,
  roles?: string[] | null
): boolean {
  return (
    isHumanRsReviewFilterEnabled(queueConfig) && isRecordSpecialistRole(roles)
  );
}

/**
 * Whether to render the Queue tab menu.
 * If tabs include Escalated, only Record Specialists see them.
 */
export function shouldShowQueueTableTabs(
  queueConfig?: QueueConfig | null,
  roles?: string[] | null
): boolean {
  if (!hasQueueTableTabs(queueConfig)) {
    return false;
  }
  if (isHumanRsReviewFilterEnabled(queueConfig)) {
    return isRecordSpecialistRole(roles);
  }
  return true;
}

/** Drop Escalated tile(s) for non–Record Specialist roles. */
export function filterQueueAnalyticsCardsForRole(
  analyticsCard: AnalyticsCard[] | undefined,
  roles?: string[] | null
): AnalyticsCard[] {
  const cards = analyticsCard || [];
  if (isRecordSpecialistRole(roles)) {
    return [...cards];
  }
  return cards.filter((card) => {
    if (getTabFilterKey(card) === QUEUE_TAB_NEEDS_HUMAN_RS_REVIEW) {
      return false;
    }
    return card.title !== "Escalated for Human Review";
  });
}

/**
 * Default needsHumanRsReview query value from tableTabs (default: true tab).
 * true when default tab's filterKey is needs_human_rs_review; else undefined (All).
 * Callers must also gate with isHumanRsReviewUiEnabled.
 */
export function getDefaultNeedsHumanRsReview(
  queueConfig?: QueueConfig | null
): boolean | undefined {
  if (!isHumanRsReviewFilterEnabled(queueConfig)) {
    return undefined;
  }
  const tabs = [...(queueConfig?.tableTabs || [])].sort(
    (a, b) => (a.index ?? 0) - (b.index ?? 0)
  );
  const defaultTab = tabs.find((tab) => tab.default) || tabs[0];
  return getTabFilterKey(defaultTab || {}) === QUEUE_TAB_NEEDS_HUMAN_RS_REVIEW
    ? true
    : undefined;
}

/** Builds Queue ClaimTabs via shared getTabs (All Claims helper). */
export function getQueueTabs(
  queueConfig: QueueConfig | undefined | null,
  needsHumanRsReview: boolean | undefined,
  roles?: string[] | null
) {
  if (!shouldShowQueueTableTabs(queueConfig, roles)) {
    return [];
  }
  if (needsHumanRsReview === true) {
    return getTabs(
      queueConfig?.tableTabs || [],
      {},
      {
        activeIdentifier: QUEUE_TAB_NEEDS_HUMAN_RS_REVIEW,
      }
    );
  }
  if (needsHumanRsReview === false) {
    return getTabs(
      queueConfig?.tableTabs || [],
      {},
      {
        activeIdentifier: "",
      }
    );
  }
  // First load / unset: honor tableTabs[].default (Queue-only flag on getTabs)
  return getTabs(
    queueConfig?.tableTabs || [],
    {},
    {
      applyConfigDefault: true,
    }
  );
}

/**
 * Queue tile counts — keep the existing title→field switch so SLA clients
 * (e.g. i0034) and "Total Available for Review" stay unchanged. New Escalated
 * / availableForReviewCount cases are additive only.
 */
export const normalizedClaimAnalyics = (
  records: any,
  queueConfig?: QueueConfig,
  claimTitles?: any,
  version = 1
): Array<ClaimInfo> => {
  const clearedRecords: QueueQueryRecord = clearTypeNameKey(records) || {};
  if (queueConfig) {
    const { analyticsCard } = queueConfig;
    if (analyticsCard && Array.isArray(analyticsCard)) {
      analyticsCard.sort((a, b) => a.index - b.index);
      return analyticsCard.map((analytics: AnalyticsCard): ClaimInfo => {
        let count = 0;
        if (version === 1) {
          if (clearedRecords?.queueClaims?.totalElements) {
            count = clearedRecords.queueClaims.totalElements;
          }
        } else {
          const queueClaims = clearedRecords?.claimLevelQueueClaims;
          if (queueClaims?.totalElements) {
            count = queueClaims.totalElements;
          }
          switch (analytics.title) {
            case "Missed SLA":
              count = queueClaims?.missedSlaCount || 0;
              break;
            case "Due 0-4 Hours":
              count = queueClaims?.sla0To4HoursCount || 0;
              break;
            case "Due 4-24 Hours":
              count = queueClaims?.sla4To24HoursCount || 0;
              break;
            case "Due 24-48 Hours":
              count = queueClaims?.sla24To48HoursCount || 0;
              break;
            case "Due 48-72 Hours":
              count = queueClaims?.sla48To72HoursCount || 0;
              break;
            case "Due 72+ Hours":
              count = queueClaims?.sla72PlusHoursCount || 0;
              break;
            case "Escalated for Human Review":
              count = queueClaims?.needsHumanRsReviewCount || 0;
              break;
            case "Total Available for Review":
            case "Available for Review":
              // Prefer stable all-queue count when present; fall back to totalElements
              // (identical for clients without the Escalated tab / filter).
              count =
                queueClaims?.availableForReviewCount ??
                queueClaims?.totalElements ??
                0;
              break;
            default: {
              // Cards keyed by filterKey (Queue) or overjetReviewResult (legacy)
              const cardKey = getTabFilterKey(analytics);
              if (cardKey === QUEUE_TAB_NEEDS_HUMAN_RS_REVIEW) {
                count = queueClaims?.needsHumanRsReviewCount || 0;
              } else if (cardKey === "Total") {
                count =
                  queueClaims?.availableForReviewCount ??
                  queueClaims?.totalElements ??
                  0;
              }
              break;
            }
          }
        }

        return {
          title: analytics.title,
          color:
            analytics.color ||
            claimTitles[getTabFilterKey(analytics)] ||
            claimTitles[analytics.title] ||
            claimTitles.Total,
          count,
        };
      });
    }
  }
  return [];
};

/**
 * Normalize claim data with versioning of API
 * @param {any} records - The data from API
 * @param {number} version - The API version
 * @returns ClaimDataObj
 */
export const normalizeClaimData = (
  records: any,
  version = 1
): ClaimDataObj | GenericObject => {
  const filteredRecord: QueueQueryRecord = clearTypeNameKey(records) || {};
  let contents: ClaimData = {
    content: [],
    totalElements: 0,
    totalPages: 0,
  };
  if (version === 1 && filteredRecord.queueClaims) {
    contents = filteredRecord.queueClaims;
  } else if (version === 2 && filteredRecord.claimLevelQueueClaims) {
    contents = filteredRecord.claimLevelQueueClaims;
  }
  if (contents.content.length > 0) {
    contents.content.map(({ records: record, ...rest }) => ({
      record,
      ...rest,
    }));
    return {
      claims: {
        ...contents,
      },
    };
  }
  return { claims: {} };
};

export const getLocalConfig = (
  updatedConfig: any,
  procedureType: unknown,
  queueTableConfig: any,
  queueFilters?: Record<string, ClientFilterConfig>
) => {
  return {
    startDate: updatedConfig.startDate,
    endDate: updatedConfig.endDate,
    search: updatedConfig.search,
    sort: updatedConfig.sort,
    procedureType: normalizeProcedureType(procedureType),
    page: isEmpty(updatedConfig.page)
      ? queueTableConfig.page
      : updatedConfig.page,
    systemFilter: updatedConfig.systemFilter,
    specialtyReviewFilter: updatedConfig.specialtyReviewFilter,
    dateRangeFilterField: updatedConfig.dateRangeFilterField,
    needsHumanRsReview: updatedConfig.needsHumanRsReview,
    ...pickLegacyQueueFilterValues(updatedConfig),
    ...pickDynamicFilterPatch(
      updatedConfig,
      queueFilters,
      RESERVED_QUEUE_SCALAR_FIELDS
    ),
  };
};
